"use server";

import { headers } from "next/headers";

const BASE = process.env.BACKEND_INTERNAL_URL ?? "http://erp-backend:4000";

export type SignupStatus = {
  enabled: boolean;
  tos: { version: string };
  dpa: { version: string };
};

export type SignupSubmitResult =
  | { status: "ok" }
  | { status: "slug_taken" }
  | { status: "invalid"; message: string }
  | { status: "disabled" }
  | { status: "error" };

export type ConfirmSignupResult =
  | { status: "ok"; slug: string; email: string }
  | { status: "slug_taken" }
  | { status: "expired" }
  | { status: "invalid" }
  | { status: "disabled" }
  | { status: "error" };

/** The server action talks to the backend container directly, so the real
 * client IP must ride along — nginx already stamped x-forwarded-for on the
 * browser request and the backend trusts exactly one proxy hop (ALTCHA fix
 * pattern). Throttling and consent evidence both depend on it. */
async function forwardHeaders(): Promise<Record<string, string>> {
  const h = await headers();
  const xff = h.get("x-forwarded-for");
  return {
    "Content-Type": "application/json",
    ...(xff ? { "x-forwarded-for": xff } : {}),
  };
}

function firstMessage(body: unknown): string {
  const message = (body as { message?: string | string[] } | null)?.message;
  if (Array.isArray(message)) return message[0] ?? "Nieprawidłowe dane.";
  return message ?? "Nieprawidłowe dane.";
}

export async function getSignupStatusAction(): Promise<SignupStatus | null> {
  try {
    const res = await fetch(`${BASE}/signup/status`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as SignupStatus;
  } catch {
    return null;
  }
}

export async function signupAction(input: {
  companyName: string;
  nip: string;
  slug: string;
  adminName: string;
  email: string;
  password: string;
  consentsAccepted: boolean;
  altcha?: string;
}): Promise<SignupSubmitResult> {
  try {
    const res = await fetch(`${BASE}/signup`, {
      method: "POST",
      headers: await forwardHeaders(),
      body: JSON.stringify(input),
      cache: "no-store",
    });
    if (res.ok) return { status: "ok" };
    if (res.status === 409) return { status: "slug_taken" };
    if (res.status === 403) return { status: "disabled" };
    if (res.status === 400 || res.status === 422) {
      return {
        status: "invalid",
        message: firstMessage(await res.json().catch(() => null)),
      };
    }
    return { status: "error" };
  } catch {
    return { status: "error" };
  }
}

export async function confirmSignupAction(
  token: string,
  slugOverride?: string,
): Promise<ConfirmSignupResult> {
  try {
    const res = await fetch(`${BASE}/signup/confirm`, {
      method: "POST",
      headers: await forwardHeaders(),
      body: JSON.stringify({
        token,
        ...(slugOverride ? { slugOverride } : {}),
      }),
      cache: "no-store",
    });
    if (res.ok) {
      const body = (await res.json()) as { slug: string; email: string };
      return { status: "ok", slug: body.slug, email: body.email };
    }
    if (res.status === 409) return { status: "slug_taken" };
    if (res.status === 410) return { status: "expired" };
    if (res.status === 403) return { status: "disabled" };
    return { status: "invalid" };
  } catch {
    return { status: "error" };
  }
}
